// Comparison API service for fetching and managing comparison results
import {
  ComparisonResults,
  ComparisonSummaryStatistics,
  GetComparisonResultsRequest,
  GetComparisonResultsResponse,
  ComparisonClauseTexts,
  GetClauseTextsRequest,
  GetClauseTextsResponse,
  ComparisonFinding,
  TextDifference,
  ComparisonTextAnalysis,
  AlignmentStats,
  CompareQualityTier,
  CompareQualityReason,
  CompareComparisonStatus,
  ComparePreflightResponse,
} from '../types/comparison';
import { apiService } from './api';
import { getErrorMessage } from '../utils/helpers';

// Plan 193: shape of the GET /api/v1/documents/{id}/compare-findings response.
export interface DocumentCompareFindingsUnion {
  findings: (ComparisonFinding & { analysis_run_id: string })[];
  dismissed_count?: number;
  alignment_stats: AlignmentStats | null;
  comparison_status: CompareComparisonStatus | null;
  failure_reason: string | null;
  quality_tier: CompareQualityTier;
  quality_reason?: CompareQualityReason; // Plan 56 (#859)
  source_analysis_run_id: string | null;
}

export class ComparisonApiService {
  private baseUrl = '/api/v1/projects';

  /**
   * Plan 178 Task 9: union of all compare findings for a review doc across
   * formal + quick-action compare runs the caller owns. Each returned
   * finding carries its parent analysis_run_id (used by ChangeDetailView).
   *
   * Plan 193: response also carries alignment_stats, comparison_status,
   * failure_reason, quality_tier, and source_analysis_run_id so the
   * compare layer can render a match-quality banner and stamp the
   * correct source on layer registration.
   */
  async getDocumentCompareFindingsUnion(
    documentId: string,
    includeDismissed: boolean = false,
  ): Promise<DocumentCompareFindingsUnion> {
    try {
      const url = includeDismissed
        ? `/api/v1/documents/${documentId}/compare-findings?include_dismissed=true`
        : `/api/v1/documents/${documentId}/compare-findings`;
      const response = await apiService.get<DocumentCompareFindingsUnion>(url);
      if (!response) throw new Error('No comparison findings received');
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch document compare findings: ${getErrorMessage(error)}`);
    }
  }

  /**
   * Plan 195: pre-flight check before starting a compare workflow.
   * Runs the existing deterministic alignment (no LLM, ~1-2s) and returns
   * a quality tier so the modal can warn the user before they commit.
   */
  async comparePreflight(
    baseDocumentId: string,
    reviewDocumentId: string,
  ): Promise<ComparePreflightResponse> {
    try {
      const response = await apiService.post<ComparePreflightResponse>(
        '/api/v1/documents/compare-preflight',
        {
          base_document_id: baseDocumentId,
          review_document_id: reviewDocumentId,
        },
      );
      if (!response) throw new Error('No preflight response received');
      return response;
    } catch (error) {
      throw new Error(`compare-preflight failed: ${getErrorMessage(error)}`);
    }
  }

  /**
   * Fetch comparison results for a project with optional filtering
   */
  async getComparisonResults(params: GetComparisonResultsRequest): Promise<ComparisonResults> {
    const { project_id, ...queryParams } = params;

    // Build query string
    const searchParams = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const queryString = searchParams.toString();

    try {
      const url = `${this.baseUrl}/${project_id}/comparison-results${queryString ? `?${queryString}` : ''}`;
      const response = await apiService.get<GetComparisonResultsResponse>(url);
      if (!response) throw new Error('No comparison results received');
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch comparison results: ${getErrorMessage(error)}`);
    }
  }

  /**
   * Get summary statistics for comparison results
   */
  async getComparisonStatistics(projectId: string): Promise<ComparisonSummaryStatistics> {
    try {
      const response = await apiService.get<ComparisonSummaryStatistics>(
        `${this.baseUrl}/${projectId}/comparison-statistics`
      );

      if (!response) {
        throw new Error('No comparison statistics received');
      }

      return response;
    } catch (error) {
      console.error('Error fetching comparison statistics:', error);
      throw new Error(`Failed to fetch comparison statistics: ${getErrorMessage(error)}`);
    }
  }

  /**
   * Update user decision for a comparison finding
   */
  async updateFindingDecision(
    projectId: string,
    findingId: string,
    decision: 'ACCEPTED' | 'REJECTED' | 'PENDING',
    notes?: string
  ): Promise<void> {
    try {
      await apiService.put(
        `${this.baseUrl}/${projectId}/comparison-findings/${findingId}/decision`,
        {
          user_decision: decision,
          user_notes: notes
        }
      );
    } catch (error) {
      console.error('Error updating finding decision:', error);
      throw new Error(`Failed to update finding decision: ${getErrorMessage(error)}`);
    }
  }

  /**
   * Bulk update decisions for multiple findings
   */
  async bulkUpdateFindingDecisions(
    projectId: string,
    updates: Array<{
      finding_id: string;
      user_decision: 'ACCEPTED' | 'REJECTED' | 'PENDING';
      user_notes?: string;
    }>
  ): Promise<void> {
    try {
      await apiService.post(
        `${this.baseUrl}/${projectId}/comparison-findings/bulk-update`,
        { updates }
      );
    } catch (error) {
      console.error('Error bulk updating finding decisions:', error);
      throw new Error(`Failed to bulk update finding decisions: ${getErrorMessage(error)}`);
    }
  }

  /**
   * Check if comparison analysis is complete for a project
   */
  async getComparisonStatus(projectId: string): Promise<{
    status: 'pending' | 'processing' | 'completed' | 'failed';
    progress?: number;
    estimated_completion?: string;
    error_message?: string;
  }> {
    try {
      const response = await apiService.get(
        `${this.baseUrl}/${projectId}/comparison-status`
      );

      if (!response) {
        throw new Error('No comparison status received');
      }

      return response;
    } catch (error) {
      console.error('Error fetching comparison status:', error);
      throw new Error(`Failed to fetch comparison status: ${getErrorMessage(error)}`);
    }
  }

  /**
   * Get clause texts for side-by-side comparison
   */
  async getClauseTexts(params: GetClauseTextsRequest): Promise<ComparisonClauseTexts> {
    try {
      const { project_id, finding_id } = params;
      const response = await apiService.get<GetClauseTextsResponse>(
        `${this.baseUrl}/${project_id}/comparison-findings/${finding_id}/clause-texts`
      );

      if (!response) {
        throw new Error('No clause texts received');
      }

      return response;
    } catch (error) {
      console.error('Error fetching clause texts:', error);
      throw new Error(`Failed to fetch clause texts: ${getErrorMessage(error)}`);
    }
  }

  /**
   * Update comparison finding with inline edits
   */
  async updateFindingWithEdit(
    projectId: string,
    findingId: string,
    updates: {
      difference_description?: string;
      risk_assessment?: string;
      business_impact?: string;
      recommendation?: string;
      legal_significance?: 'HIGH' | 'MEDIUM' | 'LOW';
      user_notes?: string;
    }
  ): Promise<ComparisonFinding> {
    try {
      const response = await apiService.put<ComparisonFinding>(
        `${this.baseUrl}/${projectId}/comparison-findings/${findingId}`,
        updates
      );

      if (!response) {
        throw new Error('No updated finding received');
      }

      return response;
    } catch (error) {
      console.error('Error updating finding:', error);
      throw new Error(`Failed to update finding: ${getErrorMessage(error)}`);
    }
  }

  /**
   * Plan 180: soft-delete a single comparison finding. Persistent server-side.
   * `projectId` is the parent analysis_run_id; supplied per-mutation because
   * the union endpoint returns findings spanning multiple runs.
   */
  async dismissFinding(projectId: string, findingId: string): Promise<void> {
    await apiService.patch(
      `${this.baseUrl}/${projectId}/comparison-findings/${findingId}/dismiss`,
      {},
    );
  }

  /** Plan 180: undo a previous dismiss. */
  async restoreFinding(projectId: string, findingId: string): Promise<void> {
    await apiService.patch(
      `${this.baseUrl}/${projectId}/comparison-findings/${findingId}/restore`,
      {},
    );
  }

  /**
   * Cache for clause texts to avoid repeated API calls
   */
  private clauseTextCache = new Map<string, ComparisonClauseTexts>();

  /**
   * Get clause texts with caching
   */
  async getCachedClauseTexts(params: GetClauseTextsRequest): Promise<ComparisonClauseTexts> {
    const cacheKey = `${params.project_id}_${params.finding_id}`;

    if (this.clauseTextCache.has(cacheKey)) {
      return this.clauseTextCache.get(cacheKey)!;
    }

    const result = await this.getClauseTexts(params);
    this.clauseTextCache.set(cacheKey, result);

    return result;
  }

  /**
   * Clear clause text cache
   */
  clearClauseTextCache(): void {
    this.clauseTextCache.clear();
  }

  /**
   * Analyze text differences between base and review clauses
   */
  analyzeTextDifferences(
    baseText?: string,
    reviewText?: string
  ): ComparisonTextAnalysis {
    if (!baseText && !reviewText) {
      return {
        differences: [],
        total_changes: 0,
        additions_count: 0,
        deletions_count: 0,
        modifications_count: 0
      };
    }

    // Simple word-based diff algorithm
    const baseWords = baseText ? baseText.split(/\s+/) : [];
    const reviewWords = reviewText ? reviewText.split(/\s+/) : [];

    const differences: TextDifference[] = [];
    let additions = 0;
    let deletions = 0;
    let modifications = 0;

    // Handle case where only one text exists
    if (!baseText) {
      differences.push({
        type: 'added',
        review_text: reviewText,
        line_number: 1
      });
      additions = reviewWords.length;
    } else if (!reviewText) {
      differences.push({
        type: 'deleted',
        base_text: baseText,
        line_number: 1
      });
      deletions = baseWords.length;
    } else {
      // Simple comparison for modifications
      if (baseText !== reviewText) {
        differences.push({
          type: 'modified',
          base_text: baseText,
          review_text: reviewText,
          line_number: 1
        });
        modifications = 1;
      } else {
        differences.push({
          type: 'unchanged',
          base_text: baseText,
          review_text: reviewText,
          line_number: 1
        });
      }
    }

    const similarity = baseText && reviewText ?
      (baseText === reviewText ? 1.0 : 0.5) : 0.0;

    return {
      differences,
      similarity_score: similarity,
      total_changes: additions + deletions + modifications,
      additions_count: additions,
      deletions_count: deletions,
      modifications_count: modifications
    };
  }
}

// Create and export a singleton instance
export const comparisonApiService = new ComparisonApiService();

// Export additional utility functions for comparison data
export const comparisonHelpers = {
  /**
   * Calculate risk distribution from comparison findings
   */
  calculateRiskDistribution(statistics: any): { high: number; medium: number; low: number } {
    const by_risk = statistics.by_risk || {};
    return {
      high: by_risk.HIGH || by_risk.high || 0,
      medium: by_risk.MEDIUM || by_risk.medium || 0,
      low: by_risk.LOW || by_risk.low || 0
    };
  },

  /**
   * Format difference type for display
   */
  formatDifferenceType(type: string): string {
    switch (type.toLowerCase()) {
      case 'missing_clauses':
        return 'Missing Clauses';
      case 'modifications':
        return 'Modifications';
      case 'additions':
        return 'Additions';
      default:
        return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  },

  /**
   * Get risk level color for UI display
   */
  getRiskLevelColor(risk: string): string {
    switch (risk?.toUpperCase()) {
      case 'HIGH':
        return 'text-danger bg-danger-soft border-danger-soft';
      case 'MEDIUM':
        return 'text-warning bg-warning-soft border-warning-soft';
      case 'LOW':
        return 'text-success bg-success-soft border-success-soft';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  },

  /**
   * Sort comparison findings by various criteria
   */
  sortFindings(findings: any[], sortBy: string, direction: 'asc' | 'desc' = 'desc') {
    return [...findings].sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case 'risk_level':
          const riskOrder = { high: 3, medium: 2, low: 1, HIGH: 3, MEDIUM: 2, LOW: 1 };
          const aRisk = (a.legal_significance || '').toLowerCase();
          const bRisk = (b.legal_significance || '').toLowerCase();
          aVal = riskOrder[aRisk as keyof typeof riskOrder] || 0;
          bVal = riskOrder[bRisk as keyof typeof riskOrder] || 0;
          break;
        case 'confidence_score':
          aVal = a.confidence_score || 0;
          bVal = b.confidence_score || 0;
          break;
        case 'clause_id':
          aVal = a.base_clause_id || a.review_clause_id || '';
          bVal = b.base_clause_id || b.review_clause_id || '';
          break;
        case 'finding_type':
          aVal = a.finding_type || '';
          bVal = b.finding_type || '';
          break;
        default:
          aVal = a.created_at || '';
          bVal = b.created_at || '';
      }

      if (direction === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
  }
};